// Heart Games Scoring Service
// Handles MBTI scoring logic and personality type determination

import { GameAnswer, HeartScore, MBTIMapping, PersonalityTrait } from '../../types/heartGames';

export class ScoringService {
  // MBTI type definitions
  private static readonly MBTI_TYPES = {
    'ENFP': { name: 'The Campaigner', description: 'Enthusiastic, creative, and sociable' },
    'ENFJ': { name: 'The Protagonist', description: 'Charismatic, inspiring, and natural-born leaders' },
    'ENTP': { name: 'The Debater', description: 'Smart, curious, and quick-thinking' },
    'ENTJ': { name: 'The Commander', description: 'Bold, decisive, and strong-willed' },
    'ESFP': { name: 'The Entertainer', description: 'Spontaneous, energetic, and enthusiastic' },
    'ESFJ': { name: 'The Consul', description: 'Caring, social, and popular' },
    'ESTP': { name: 'The Entrepreneur', description: 'Smart, energetic, and perceptive' },
    'ESTJ': { name: 'The Executive', description: 'Dedicated, strong-willed, and practical' },
    'INFP': { name: 'The Mediator', description: 'Poetic, kind, and altruistic' },
    'INFJ': { name: 'The Advocate', description: 'Creative, insightful, and principled' },
    'INTP': { name: 'The Thinker', description: 'Innovative, independent, and strategic' },
    'INTJ': { name: 'The Architect', description: 'Imaginative, strategic, and independent' },
    'ISFP': { name: 'The Adventurer', description: 'Flexible, charming, and sensitive' },
    'ISFJ': { name: 'The Protector', description: 'Very dedicated, warm, and protective' },
    'ISTP': { name: 'The Virtuoso', description: 'Bold, practical, and experimental' },
    'ISTJ': { name: 'The Logistician', description: 'Practical, fact-minded, and reliable' }
  };

  // Personality traits with descriptions
  private static readonly PERSONALITY_TRAITS: PersonalityTrait[] = [
    { name: 'Extraversion', value: 0, description: 'Gain energy from social interaction', examples: ['Outgoing', 'Talkative', 'Enthusiastic'] },
    { name: 'Introversion', value: 0, description: 'Gain energy from quiet reflection', examples: ['Thoughtful', 'Reserved', 'Independent'] },
    { name: 'Sensing', value: 0, description: 'Focus on concrete information and details', examples: ['Practical', 'Realistic', 'Detail-oriented'] },
    { name: 'Intuition', value: 0, description: 'Focus on patterns and possibilities', examples: ['Imaginative', 'Abstract', 'Future-focused'] },
    { name: 'Thinking', value: 0, description: 'Make decisions based on logic and analysis', examples: ['Analytical', 'Objective', 'Fair'] },
    { name: 'Feeling', value: 0, description: 'Make decisions based on values and harmony', examples: ['Empathetic', 'Compassionate', 'Harmonious'] },
    { name: 'Judging', value: 0, description: 'Prefer structure and planned approaches', examples: ['Organized', 'Decisive', 'Structured'] },
    { name: 'Perceiving', value: 0, description: 'Prefer flexibility and spontaneous approaches', examples: ['Adaptable', 'Spontaneous', 'Flexible'] }
  ];

  /**
   * Calculate a REAL MBTI score by summing the chosen options' MBTI weights.
   * Unlike `calculateScore` (which uses random simulation), this reads each
   * answer's actual option mapping from the question set, so the result is
   * deterministic and meaningful for matching.
   */
  static computeScoreFromQuestions(
    answers: GameAnswer[],
    questions: Array<{ id: string; options: Array<{ id: string; mbtiWeight?: MBTIMapping }> }>
  ): HeartScore {
    const totals = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    const questionById = new Map(questions.map((q) => [q.id, q]));

    for (const answer of answers) {
      const question = questionById.get(answer.questionId);
      const option = question?.options.find((o) => o.id === answer.optionId);
      const w = option?.mbtiWeight;
      if (!w) continue;
      totals.E += w.E || 0; totals.I += w.I || 0;
      totals.S += w.S || 0; totals.N += w.N || 0;
      totals.T += w.T || 0; totals.F += w.F || 0;
      totals.J += w.J || 0; totals.P += w.P || 0;
    }

    const base: HeartScore = { ...totals, type: 'UNKNOWN', confidence: 0, traits: [] };
    return {
      ...totals,
      type: this.determineType(base),
      confidence: this.calculateConfidence(base),
      traits: this.generateTraits(totals),
    };
  }

  /**
   * Calculate MBTI score from game answers
   */
  static calculateScore(answers: GameAnswer[]): HeartScore {
    if (answers.length === 0) {
      return this.getEmptyScore();
    }

    // Initialize score counters
    const scores = {
      E: 0, I: 0,
      S: 0, N: 0,
      T: 0, F: 0,
      J: 0, P: 0
    };

    // Calculate scores from answers
    answers.forEach(answer => {
      // In a real implementation, this would look up the question's MBTI mapping
      // For now, we'll simulate scoring based on response patterns
      const responseWeight = this.calculateResponseWeight(answer);
      
      // Simulate MBTI weight distribution based on response patterns
      this.distributeMBTIWeights(scores, answer, responseWeight);
    });

    // Determine personality type
    const type = this.determineType({
      E: scores.E,
      I: scores.I,
      S: scores.S,
      N: scores.N,
      T: scores.T,
      F: scores.F,
      J: scores.J,
      P: scores.P,
      type: 'UNKNOWN',
      confidence: 0,
      traits: []
    });
    
    // Calculate confidence
    const confidence = this.calculateConfidence({
      E: scores.E,
      I: scores.I,
      S: scores.S,
      N: scores.N,
      T: scores.T,
      F: scores.F,
      J: scores.J,
      P: scores.P,
      type: 'UNKNOWN',
      confidence: 0,
      traits: []
    });
    
    // Generate traits
    const traits = this.generateTraits(scores);

    return {
      E: scores.E,
      I: scores.I,
      S: scores.S,
      N: scores.N,
      T: scores.T,
      F: scores.F,
      J: scores.J,
      P: scores.P,
      type,
      confidence,
      traits
    };
  }

  /**
   * Human-readable name + description for an MBTI type (e.g. "The Campaigner").
   */
  static getTypeInfo(type: string): { name: string; description: string } {
    const types = this.MBTI_TYPES as Record<string, { name: string; description: string }>;
    return types[type] ?? { name: 'Your Type', description: 'A unique blend of traits that shapes how you connect.' };
  }

  /**
   * Determine MBTI type from scores
   */
  static determineType(score: HeartScore): string {
    const { E, I, S, N, T, F, J, P } = score;
    
    const extraversion = E > I ? 'E' : 'I';
    const sensing = S > N ? 'S' : 'N';
    const thinking = T > F ? 'T' : 'F';
    const judging = J > P ? 'J' : 'P';
    
    return extraversion + sensing + thinking + judging;
  }

  /**
   * Calculate confidence level for the score
   */
  static calculateConfidence(score: HeartScore): number {
    const { E, I, S, N, T, F, J, P } = score;
    
    // Calculate the difference between opposing traits
    const extraversionDiff = Math.abs(E - I);
    const sensingDiff = Math.abs(S - N);
    const thinkingDiff = Math.abs(T - F);
    const judgingDiff = Math.abs(J - P);
    
    // Average the differences and convert to percentage
    const avgDiff = (extraversionDiff + sensingDiff + thinkingDiff + judgingDiff) / 4;
    const confidence = Math.min(95, Math.max(60, avgDiff * 10));
    
    return Math.round(confidence);
  }

  /**
   * Generate personalized insights based on score
   */
  static generateInsights(score: HeartScore): string[] {
    const insights: string[] = [];
    const { E, I, S, N, T, F, J, P } = score;
    
    // Extraversion vs Introversion insights
    if (E > I) {
      insights.push("You gain energy from social interactions and enjoy being around people.");
      insights.push("You're likely to be outgoing and expressive in social settings.");
    } else {
      insights.push("You recharge through quiet reflection and prefer meaningful one-on-one conversations.");
      insights.push("You're thoughtful and prefer to think before speaking.");
    }
    
    // Sensing vs Intuition insights
    if (S > N) {
      insights.push("You focus on concrete details and prefer practical, real-world applications.");
      insights.push("You're grounded in the present and value hands-on experience.");
    } else {
      insights.push("You see the big picture and enjoy exploring possibilities and patterns.");
      insights.push("You're imaginative and future-oriented in your thinking.");
    }
    
    // Thinking vs Feeling insights
    if (T > F) {
      insights.push("You make decisions based on logical analysis and objective criteria.");
      insights.push("You value fairness and consistency in your approach to problems.");
    } else {
      insights.push("You consider people's feelings and values when making decisions.");
      insights.push("You're empathetic and prioritize harmony in relationships.");
    }
    
    // Judging vs Perceiving insights
    if (J > P) {
      insights.push("You prefer structure and like to plan things in advance.");
      insights.push("You're organized and decisive in your approach to life.");
    } else {
      insights.push("You're flexible and adaptable, preferring to keep your options open.");
      insights.push("You enjoy spontaneity and are comfortable with uncertainty.");
    }
    
    return insights;
  }

  /**
   * Generate recommendations based on score
   */
  static generateRecommendations(score: HeartScore): string[] {
    const recommendations: string[] = [];
    const { E, I, S, N, T, F, J, P } = score;
    
    // Dating recommendations based on personality type
    if (E > I) {
      recommendations.push("Consider group activities and social events for meeting potential partners.");
      recommendations.push("Look for partners who share your enthusiasm for social interaction.");
    } else {
      recommendations.push("Focus on meaningful one-on-one conversations and intimate settings.");
      recommendations.push("Seek partners who appreciate your thoughtful and reflective nature.");
    }
    
    if (S > N) {
      recommendations.push("Plan concrete, practical dates that involve hands-on activities.");
      recommendations.push("Look for partners who value tradition and stability.");
    } else {
      recommendations.push("Explore creative and unconventional date ideas together.");
      recommendations.push("Seek partners who share your imagination and curiosity.");
    }
    
    if (T > F) {
      recommendations.push("Communicate clearly and directly about your expectations and boundaries.");
      recommendations.push("Look for partners who appreciate logical discussion and debate.");
    } else {
      recommendations.push("Prioritize emotional connection and shared values in relationships.");
      recommendations.push("Seek partners who are empathetic and emotionally intelligent.");
    }
    
    if (J > P) {
      recommendations.push("Plan dates in advance and communicate your preferences clearly.");
      recommendations.push("Look for partners who appreciate structure and reliability.");
    } else {
      recommendations.push("Embrace spontaneity and be open to last-minute plans.");
      recommendations.push("Seek partners who are flexible and adaptable.");
    }
    
    return recommendations;
  }

  /**
   * Compatibility with other personality types.
   *
   * DERIVED FROM THE LETTERS, not a lookup table.
   *
   * This used to be a hand-written map with entries for exactly two types —
   * ENFP and ENFJ — and a "add more as needed" note. The other fourteen
   * returned an empty list, so 14 of 16 people finished the game and were
   * shown a compatibility section with nothing in it. A table that has to be
   * filled in by hand for 16 x 16 pairs will always have holes; a rule cannot.
   *
   * The weighting is Keirsey-style pairing heuristics, in descending order of
   * how much each axis is held to matter:
   *
   *   S/N same     +16  how two people take in the world. Shared perception is
   *                     the axis most consistently associated with getting on
   *                     — it is roughly whether you find the same things worth
   *                     talking about.
   *   E/I opposite +10  energy tends to balance rather than match: one drawn
   *                     outward, one inward.
   *   T/F opposite  +8  decisions likewise — logic and values checking each
   *                     other rather than doubling up.
   *   J/P same      +6  pace of life. Planners and improvisers can absolutely
   *                     work, but they negotiate more.
   *
   * BE CLEAR ABOUT WHAT THIS IS: a readable heuristic, not a measurement.
   * MBTI pair "compatibility" has no strong empirical support, and these
   * numbers are a rank ordering dressed as percentages. They are honest as
   * suggestions and dishonest as predictions — which is why the copy each one
   * carries says what the two types share, rather than promising an outcome.
   */
  private static readonly COMPATIBILITY_BASE = 55;

  /** True for a usable four-letter MBTI type. */
  static isValidType(type?: string | null): boolean {
    return !!type && type.length === 4 && type !== 'UNKNOWN' && type in this.MBTI_TYPES;
  }

  /**
   * Compatibility of one type with another — the single implementation of the
   * rule above. Everything that ranks by compatibility goes through here,
   * whether it is scoring the sixteen types or scoring real people, so the two
   * can never disagree about what a 95 means.
   */
  static compareTypes(
    userType: string,
    otherType: string,
  ): { score: number; description: string } | null {
    if (!this.isValidType(userType) || !this.isValidType(otherType)) return null;

    const [uEI, uSN, uTF, uJP] = userType.split('');
    const [tEI, tSN, tTF, tJP] = otherType.split('');
    const samePerception = uSN === tSN;
    const oppositeEnergy = uEI !== tEI;
    const oppositeDecisions = uTF !== tTF;
    const samePace = uJP === tJP;

    const score = this.COMPATIBILITY_BASE +
      (samePerception ? 16 : 0) +
      (oppositeEnergy ? 10 : 0) +
      (oppositeDecisions ? 8 : 0) +
      (samePace ? 6 : 0);

    // Say what the pairing actually has going for it. A bare percentage
    // invites the reader to treat it as a measurement; naming the reasons
    // keeps it a suggestion they can judge for themselves.
    const reasons: string[] = [];
    if (samePerception) reasons.push('you take in the world the same way');
    if (oppositeEnergy) reasons.push('their energy balances yours');
    if (oppositeDecisions) reasons.push('they weigh decisions differently to you');
    if (samePace) reasons.push('you live at the same pace');

    const description = reasons.length
      ? reasons[0].charAt(0).toUpperCase() + reasons[0].slice(1) +
        (reasons.length > 1 ? `, and ${reasons.slice(1).join(', ')}` : '') + '.'
      : 'Little natural overlap — this one takes deliberate effort on both sides.';

    return { score, description };
  }

  static getCompatibilityScores(userType: string): { type: string; score: number; description: string }[] {
    if (!this.isValidType(userType)) return [];

    return Object.keys(this.MBTI_TYPES)
      .filter((type) => type !== userType)
      .map((type) => ({ type, ...this.compareTypes(userType, type)! }))
      // Highest first, then alphabetically so equal scores have a stable order
      // rather than shuffling between renders.
      .sort((a, b) => b.score - a.score || a.type.localeCompare(b.type))
      .slice(0, 4);
  }

  // Private helper methods
  private static getEmptyScore(): HeartScore {
    return {
      E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0,
      type: 'UNKNOWN',
      confidence: 0,
      traits: []
    };
  }

  private static calculateResponseWeight(answer: GameAnswer): number {
    // Calculate weight based on response time and confidence
    const baseWeight = 1.0;
    const timeWeight = Math.max(0.5, Math.min(1.5, answer.responseTime / 1000));
    const confidenceWeight = answer.confidence ? answer.confidence / 100 : 1.0;
    
    return baseWeight * timeWeight * confidenceWeight;
  }

  private static distributeMBTIWeights(scores: any, answer: GameAnswer, weight: number): void {
    // Simulate MBTI weight distribution based on response patterns
    // In a real implementation, this would use the actual question's MBTI mapping
    
    // Random distribution for simulation
    const random = Math.random();
    
    if (random < 0.3) {
      scores.E += weight;
    } else if (random < 0.6) {
      scores.I += weight;
    }
    
    if (random < 0.4) {
      scores.S += weight;
    } else if (random < 0.8) {
      scores.N += weight;
    }
    
    if (random < 0.5) {
      scores.T += weight;
    } else if (random < 1.0) {
      scores.F += weight;
    }
    
    if (random < 0.6) {
      scores.J += weight;
    } else if (random < 1.0) {
      scores.P += weight;
    }
  }

  private static generateTraits(scores: any): PersonalityTrait[] {
    return this.PERSONALITY_TRAITS.map(trait => ({
      ...trait,
      value: scores[trait.name.charAt(0)] || 0
    }));
  }
}
